import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { GenerateUrlRequestBody, Paginated, PaginationQuery, UpdateVisibilityRequestBody } from "./../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import {
  AdminGlobalSteelVersion,
  InsertSteelVersionBody,
  UpdateSteelVersionNewImageBody,
  UpdateSteelVersionRemoveImageBody,
} from "./types/globalSteelVersions";

export const useSteelVersionsApi = () => {
  const api = useApi();

  const getAdminSteelVersion = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/global_settings/steel_versions`;
      return useSWRMutation<AdminGlobalSteelVersion, CustomError, string, string>(
        url,
        (url, { arg }: { arg: string }) => api.get<AdminGlobalSteelVersion>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminSteelVersions = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/global_settings/steel_versions", query);
      return useSWR<Paginated<AdminGlobalSteelVersion>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminGlobalSteelVersion>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminSteelVersion = useCallback(
    async (body: InsertSteelVersionBody): Promise<{ id: string }> => {
      const url = `/admin/global_settings/steel_versions`;
      const id = await api.post<{ id: string }, InsertSteelVersionBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminSteelVersion = useCallback(
    async (
      steelVersionId: string,
      body: UpdateSteelVersionRemoveImageBody | UpdateSteelVersionNewImageBody,
    ): Promise<void> => {
      const url = `/admin/global_settings/steel_versions/${steelVersionId}`;
      (await api.put<UpdateSteelVersionRemoveImageBody | UpdateSteelVersionNewImageBody>(url, body),
        mutateCache([`/admin/global_settings/steel_versions`]));
    },
    [api],
  );

  const deleteAdminSteelVersion = useCallback(
    async (steelVersionId: string): Promise<void> => {
      const url = `/admin/global_settings/steel_versions/${steelVersionId}`;
      await api.del(url);
      mutateCache([
        `/admin/global_settings/steel_versions`,
        `/admin/settings/vertigrip/steel_versions`,
        "/admin/settings/patrol/steel_versions",
      ]);
    },
    [api],
  );

  const updateAdminSteelVersionVisibility = useCallback(
    async (steelVersionId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/global_settings/steel_versions/${steelVersionId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([`/admin/global_settings/steel_versions`]);
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (installationTypeId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/global_settings/steel_versions/${installationTypeId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  return {
    getAdminSteelVersion,
    useAdminSteelVersions,
    insertAdminSteelVersion,
    updateAdminSteelVersion,
    updateAdminSteelVersionVisibility,
    deleteAdminSteelVersion,
    generateUploadImageUrl,
  };
};
